import { useEffect, useState } from 'react'
import { useConfig, useCreateFlow, useFlowPreset, useFlows } from 'api'
import { Feature, FlowAction, FlowData, FlowFormSchema, FlowInterval, FlowTrigger, flowBodyExtensions } from 'types'
import { Loading, PageError } from 'components/styled'
import { getDefaultExtensionIndex } from 'components/flow/flow-template/flow-template'
import { beautifyJsonString, getShopId, queryClient, useTranslate } from 'utils'
import { useNavigate, useParams } from 'react-router-dom'
import { FlowForm } from 'pages/flow/flow-form/flow-form'
import { message } from 'antd'
import { setIn } from 'final-form'

export const CreateFlow = () => {
    
    //hooks:
    const { data: config } = useConfig()
    const params = useParams()
    const navigate = useNavigate()
    
    //api:
    const { data: flows, total, isLoading: isFlowsLoading } = useFlows(null)

    // redirect if it doesn't have access to flow:
    useEffect(() => {
        if(config && flows){
            if(!config.features.includes(Feature.FLOWS)){
                navigate('/')
            }else if(flows && (total ?? 0) >= (config.plan?.maxFlows ?? 0)){
                navigate('/flow')
            }
        }
    }, [config, flows, total])

    return (
        isFlowsLoading ? <Loading/> :
        !flows ? <PageError/> :
        params.presetId ? <CreateFlowWithPreset presetId={params.presetId}/> :
        <CreateFlowController preset={defaultValues}/>
    )
    
}

type CreateFlowWithPresetProps = {
    presetId: string
}

const CreateFlowWithPreset = ({ presetId }: CreateFlowWithPresetProps) => {
    const { data: flowPreset, isLoading } = useFlowPreset(presetId)
    return isLoading ? <Loading/> : !flowPreset ? <PageError/> :
        <CreateFlowController preset={flowPreset.data}/>
}

type CreateFlowControllerProps = {
    preset: FlowData
}

const CreateFlowController = ({ preset }: CreateFlowControllerProps) => {
    
    //translate:
    const { __ } = useTranslate()

    //navigation:
    const navigate = useNavigate()

    //state:
    const createInitialValues = (values: FlowData) => ({
        ...values,
        template: {
            headers: beautifyJsonString(preset.template.headers),
            body: beautifyJsonString(preset.template.body)
        }
    })
    const [ initialValues, setInitialValues ] = useState(createInitialValues(preset))
    const [ savingValues, setSavingValues ] = useState(preset)
    const [ action, setAction ] = useState(preset.action)
    const [ bodyExt, setBodyExt ] = useState(flowBodyExtensions[getDefaultExtensionIndex(preset.action)][0])

    //validateFormValues:
    const validateFormValues = (schema: any) => async (values: FlowData) => {
        if(values.action !== action){
            setAction(values.action)
        }
        if(typeof schema === 'function')
            schema = schema()
        try{
            await schema.validate(values, { abortEarly: false })
        }catch(err: any){
            const errors = err.inner.reduce((formError: object, innerError: any) => {
                return setIn(formError, innerError.path, innerError.message)
            }, {})
            return errors
        }
    }

    //save:
    const create = (values: FlowData) => {
        setSavingValues(values)
        mutate()
    }

    //create:
    const { mutate, isLoading, data: response } = useCreateFlow(savingValues)

    //after create:
    useEffect(() => {
        if(response){
            setInitialValues(createInitialValues(savingValues))
            queryClient.invalidateQueries(['flows', getShopId()])
            message.success(__`flow_created`)
            setTimeout(() => {
                navigate('/flow')
            }, 100)
        }
    }, [isLoading])

    return (
        <FlowForm
            initialValues={initialValues}
            onBodyExtensionChanged={i => setBodyExt(flowBodyExtensions[i][0])}
            isCreate={true}
            isSaving={isLoading}
            onValidate={validateFormValues(FlowFormSchema(action, bodyExt as string, __))}
            onSave={values => (
                FlowFormSchema(action, bodyExt as string, __).validate(values).then(
                    data => create(data as FlowData)
                )
            )}
            onBack={() => navigate('/flow/presets')}
        />
    )
    
}

//default values:
const defaultValues: FlowData = {
    name: '',
    action: FlowAction.WEBHOOK,
    target: '',
    trigger: FlowTrigger.APPOINTMENT_CREATED,
    when: {
        amount: 0,
        unit: FlowInterval.IMMEDIATELY
    },
    template: {
        headers: '{\"Content-Type\": \"application/json\"}',
        body: '{\"data\": \"hello {{CustomerName}}!\"}'
    },
    status: true
}
